import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Mail, MapPin, Send, Loader2, Lock, ArrowRight } from "lucide-react";
import { COMPANY } from "@/data/company";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Required";
    if (!form.email.trim()) er.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Enter a valid email";
    if (!form.subject.trim()) er.subject = "Required";
    if (!form.message.trim() || form.message.trim().length < 10)
      er.message = "Please provide at least 10 characters";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length > 0) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      if (res.status === 200) {
        setSubmitted(true);
        setForm(initialForm);
        toast.success(
          res.data?.email_sent
            ? "Message sent. We'll be in touch shortly."
            : "Message received. We'll be in touch shortly."
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="overline">Contact</div>
            <h1
              data-testid="contact-title"
              className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            >
              Speak directly with the firm.
            </h1>
          </div>
          <div className="lg:col-span-6">
            <p className="text-slate-700 text-lg leading-relaxed">
              For investment partnerships, co-investment proposals, banking
              correspondence, and media enquiries — share a few details and a
              principal will respond personally.
            </p>
            <Link
              to="/opportunity"
              data-testid="contact-opportunity-link"
              className="mt-6 inline-flex items-center gap-3 group border border-slate-900 px-5 py-4 hover:bg-slate-900 hover:text-white transition-colors"
            >
              <Lock size={16} className="flex-none" />
              <span className="text-[12px] tracking-[0.18em] uppercase font-semibold">
                Submitting a deal?  Use the confidential portal
              </span>
              <ArrowRight
                size={16}
                className="flex-none group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* FORM */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Full name *"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  error={errors.name}
                  testid="contact-input-name"
                  placeholder="Jane Doe"
                />
                <Field
                  label="Email *"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  error={errors.email}
                  testid="contact-input-email"
                  placeholder="jane@example.com"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  testid="contact-input-phone"
                  placeholder="+65 0000 0000"
                />
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  testid="contact-input-company"
                  placeholder="Organisation"
                />
              </div>
              <Field
                label="Subject *"
                name="subject"
                value={form.subject}
                onChange={onChange}
                error={errors.subject}
                testid="contact-input-subject"
                placeholder="Co-investment opportunity in clean energy"
              />
              <div>
                <Label
                  htmlFor="message"
                  className="overline text-slate-500 mb-2 block"
                >
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-input-message"
                  rows={6}
                  placeholder="Briefly describe your enquiry, the sectors involved, and the geography…"
                  className={`bg-white border-slate-200 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 rounded-none ${
                    errors.message ? "border-rose-400" : ""
                  }`}
                />
                {errors.message && (
                  <p
                    data-testid="contact-error-message"
                    className="mt-2 text-xs text-rose-600"
                  >
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase font-semibold bg-slate-900 text-white px-7 py-4 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      <Send size={14} /> Send message
                    </>
                  )}
                </button>
                {submitted && (
                  <span
                    data-testid="contact-success"
                    className="text-sm text-emerald-800"
                  >
                    Thank you — your message has been received.
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 leading-relaxed pt-2">
                By submitting, you consent to Finovest Holdings Ltd contacting
                you regarding your enquiry. We treat all correspondence with
                strict confidentiality.
              </p>
            </form>
          </div>

          {/* OFFICES */}
          <aside className="lg:col-span-5">
            <div className="border border-slate-200 bg-slate-50 p-8">
              <div className="overline">Direct contact</div>
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email-link"
                className="mt-4 inline-flex items-center gap-2 text-slate-900 font-serif text-xl hover:text-emerald-800 transition-colors"
              >
                <Mail size={18} />
                {COMPANY.email}
              </a>
            </div>
            <div className="mt-8 space-y-8">
              <OfficeCard
                title={COMPANY.addresses.bvi.label}
                line={COMPANY.addresses.bvi.line}
                testid="contact-office-bvi"
              />
              <OfficeCard
                title={COMPANY.addresses.singapore.label}
                line={COMPANY.addresses.singapore.line}
                testid="contact-office-singapore"
              />
            </div>
            <div className="mt-10 p-6 bg-slate-950 text-slate-300">
              <div className="overline text-slate-400">Registration</div>
              <div className="mt-3 font-serif text-lg text-white">
                {COMPANY.registration}
              </div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Registered office in the British Virgin Islands. Singapore
                operating presence at 50A Temple Street.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
  error,
  testid,
  placeholder,
}) {
  return (
    <div>
      <Label
        htmlFor={name}
        className="overline text-slate-500 mb-2 block"
      >
        {label}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        className={`bg-white border-slate-200 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 rounded-none h-12 ${
          error ? "border-rose-400" : ""
        }`}
      />
      {error && (
        <p
          data-testid={`${testid}-error`}
          className="mt-2 text-xs text-rose-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function OfficeCard({ title, line, testid }) {
  return (
    <div data-testid={testid} className="border-l-2 border-slate-900 pl-5">
      <div className="overline">{title}</div>
      <div className="mt-3 flex items-start gap-2 text-slate-900 font-serif text-lg leading-snug">
        <MapPin size={18} className="mt-1 flex-none text-slate-600" />
        <span>{line}</span>
      </div>
    </div>
  );
}
